import React, { useMemo, useCallback, useState, useEffect } from 'react';
import Timepicker from 'components/atoms/Timepicker';
import Select from 'components/atoms/Select';
import Input from 'components/atoms/Input';
import Button from 'components/atoms/Button';
import { StyledFlexbox } from 'components/atoms/Flexbox/styled';
import ExceptionConfigs from 'components/molecules/settings/ExceptionConfigs';
import { StyledBodyText } from '../../styled';
import { Title } from 'components/atoms/styleds';
import SelectV2 from 'components/atoms/Select';
import { getDayHourPerHalf } from 'utils';
import { ReactComponent as Delete } from 'assets/images/delete.svg';
import { nanoid } from 'nanoid';

const EXCEPTION_TYPES = [
  { id: 'tutto_il_giorno', title: 'Tutto il giorno' },
  { id: 'fasce_orarie', title: 'Fasce orarie' },
];

const ConfigExceptions = ({
  onSave,
  onCancel,
  selectedExceptionId,
  newExceptionData,
  setNewExceptionData,
  handleChangeSelectTime,
  handleSelect,
  onClickDelete,
  onChangeDay,
  onClickCreate,
  onFormReset,
}) => {
  const [status, setStatus] = useState('chiuso');
  const [date, setDate] = useState(new Date());
  const [exceptionType, setExceptionType] = useState('fasce_orarie');
  const [intervals, setIntervals] = useState([{ from: '09:00', to: '18:00' }]);
  const [id] = useState(() => nanoid());

  const handleAddInterval = useCallback(
    () => setIntervals((prev) => [...prev, { from: '09:00', to: '18:00' }]),
    [],
  );

  const handleRemoveInterval = useCallback(
    (idx) => setIntervals((prev) => prev.filter((_, i) => i !== idx)),
    [],
  );

  const handleIntervalChange = useCallback((idx, field, value) => {
    setIntervals((prev) =>
      prev.map((f, i) => (i === idx ? { ...f, [field]: value } : f)),
    );
  }, []);

  const hours = getDayHourPerHalf();

  // Функція для скидання форми
  const resetForm = useCallback(() => {
    setStatus('chiuso');
    setDate(new Date());
    setExceptionType('fasce_orarie');
    setIntervals([{ from: '09:00', to: '18:00' }]);
  }, []);

  // Передаємо функцію скидання в батьківський компонент
  useEffect(() => {
    if (onFormReset) {
      onFormReset(resetForm);
    }
  }, [onFormReset, resetForm]);

  // Формуємо body для API з використанням useMemo
  const exception = useMemo(() => {
    const exceptionData = {
      id: id,
      date_from: date ? date.toISOString().slice(0, 10) : null,
      date_to: date ? date.toISOString().slice(0, 10) : null,
      day_off: status === 'chiuso',
      day_off_type:
        status === 'chiuso' && exceptionType === 'tutto_il_giorno'
          ? 'full_day'
          : 'partial_day',
    };

    if (status === 'aperto') {
      exceptionData.working_intervals = intervals.map(({ from, to }) => ({
        from,
        to,
      }));
    }
    if (status === 'chiuso' && exceptionType === 'fasce_orarie') {
      exceptionData.closing_intervals = intervals.map(({ from, to }) => ({
        from,
        to,
      }));
    }

    return exceptionData;
  }, [id, date, status, exceptionType, intervals]);

  // Оновлюємо newExceptionData через useEffect
  useEffect(() => {
    setNewExceptionData(exception);
  }, [exception, setNewExceptionData]);

  return (
    <>
      <div style={{ minWidth: 320, minHeight: 530, maxWidth: 370, padding: 0 }}>
        <style>
          {`
          .custom-radio {
  appearance: none;
  width: 12px;
  height: 12px;
  border: 1px solid #ff7828;
  border-radius: 50%;
  background: #fff;
  position: relative;
  margin-right: 10px;
  cursor: pointer;
  transition: border-color 0.2s;
  display: inline-block;
  vertical-align: middle;
}
        .custom-radio:checked {
  border: 1px solid #ff7828;
  background: #fff;
}
            .custom-radio:checked::after {
  content: '';
  display: block;
  width: 8px;
  height: 8px;
  background: #ff7828;
  border-radius: 50%;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}
          .custom-radio:focus {
            outline: none;
            box-shadow: none;
          }
        `}
        </style>
        <StyledBodyText
          style={{
            textAlign: 'left',
            fontSize: 15,
            color: '#636262',
            marginBottom: 18,
          }}
        >
          Inserisci eventuali variazioni rispetto ai regolari orari del tuo
          servizio.
          <br />
          Es: aperture domenicali, chiusure per ferie, feste nazionali.
        </StyledBodyText>
        <div style={{ marginBottom: 40 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                fontWeight: 600,
                fontSize: 16,
                color: '#636262',
                cursor: 'pointer',
              }}
            >
              <input
                type='radio'
                className='custom-radio'
                checked={status === 'aperto'}
                onChange={() => setStatus('aperto')}
              />
              Aperto
            </label>
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                fontWeight: 600,
                fontSize: 16,
                color: '#636262',
                cursor: 'pointer',
              }}
            >
              <input
                type='radio'
                className='custom-radio'
                checked={status === 'chiuso'}
                onChange={() => setStatus('chiuso')}
              />
              Chiuso
            </label>
          </div>
        </div>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            marginBottom: 40,
          }}
        >
          <Timepicker
            helperText='Scegli la data'
            selectedDate={date}
            onChange={setDate}
            pickerAbsolute={true}
            style={{ alignSelf: 'center' }}
            showMinDate={false}
          />
        </div>
        {status === 'chiuso' && (
          <div style={{ marginBottom: 40 }}>
            <Select
              name='exceptionType'
              label='Indica la tipologia di chiusura'
              items={EXCEPTION_TYPES}
              selectedKey={exceptionType}
              onChange={(e) => setExceptionType(e.target.value)}
              defaultLabel='Tutto il giorno'
              fullWidth
            />
          </div>
        )}
        {(exceptionType === 'fasce_orarie' || status === 'aperto') && (
          <div
            style={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              marginBottom: 12,
              gap: 20,
            }}
          >
            {intervals.map((interval, idx) => (
              <StyledFlexbox
                key={idx}
                direction='column'
                align='center'
                justify='center'
                gap={10}
                style={{ marginBottom: 20 }}
              >
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 10,
                  }}
                >
                  <SelectV2
                    desktopMb={0}
                    style={{ width: 110 }}
                    label='Inizio'
                    onChange={(e) =>
                      handleIntervalChange(idx, 'from', e.target.value)
                    }
                    name='from'
                    items={hours}
                    selectedKey={interval.from}
                  />
                  <span
                    style={{ fontWeight: 600, fontSize: 18, color: '#636262' }}
                  >
                    –
                  </span>
                  <SelectV2
                    desktopMb={0}
                    style={{ width: 110 }}
                    label='Fine'
                    onChange={(e) =>
                      handleIntervalChange(idx, 'to', e.target.value)
                    }
                    name='to'
                    items={hours}
                    selectedKey={interval.to}
                  />
                </div>
                {intervals.length > 1 && (
                  <button
                    type='button'
                    style={{
                      border: 'none',
                      background: 'none',
                      color: '#636262',
                      fontWeight: 700,
                      fontSize: 14,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      marginTop: 20,
                      alignSelf: 'flex-end',
                    }}
                    onClick={() => handleRemoveInterval(idx)}
                  >
                    Rimuovi fascia <Delete />
                  </button>
                )}
              </StyledFlexbox>
            ))}
            <button
              type='button'
              style={{
                color: '#ff7828',
                border: 'none',
                alignSelf: 'flex-end ',
                fontWeight: 500,
                fontSize: 14,
                background: 'none',
                boxShadow: 'none',
                marginTop: -20,
                marginRight: 30,
              }}
              onClick={handleAddInterval}
            >
              + Aggiungi fascia
            </button>
          </div>
        )}
      </div>
      <div style={{ height: 1, background: '#FF7828', opacity: 0.3 }} />
    </>
  );
};

export default ConfigExceptions;
